import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Icon } from '@iconify/react/dist/iconify.js';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

// Bar-chart drill-down for the STRUCTURE (Zone -> Circle -> Division). When a
// division is selected, its complaints are shown as status-coloured CARDS, each
// linking to /complains/view/<id>.
const LEVELS = ['zone', 'circle', 'division'];
const LEVEL_COLOR = { zone: '#f59e0b', circle: '#3b82f6', division: '#22c55e' };
const LEVEL_LABEL = { zone: 'Zones', circle: 'Circles', division: 'Divisions' };

const STATUS_COLOR = {
  Open: '#d97706',
  'In Progress': '#4f46e5',
  Escalated: '#e11d48',
  Closed: '#0891b2',
  Reopened: '#7c3aed',
  Resolved: '#059669',
};

const STATUS_GRADIENT = {
  Open: ['#f59e0b', '#b45309'],
  'In Progress': ['#8e7cff', '#4f46e5'],
  Escalated: ['#fb7185', '#be123c'],
  Closed: ['#3fd4e0', '#0891b2'],
  Reopened: ['#dcd31f', '#F59E0B'],
  Resolved: ['#3edc9c', '#059669'],
};

const STATUS_ORDER = ['In Progress', 'Escalated', 'Closed', 'Reopened', 'Resolved', 'Open'];
const statusColor = (s) => STATUS_COLOR[s] || '#64748b';
const statusGradient = (s) => STATUS_GRADIENT[s] || ['#94a3b8', '#475569'];
const statusLabel = (s) => (s === 'In Progress' ? 'Assigned' : s === 'Reopened' ? 'Re-Open' : s === 'Closed' ? 'Reply Submitted' : s || '-');

const openReport = (id) => window.open(`/complains/view/${id}`, '_blank', 'noopener');

const styles = `
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@500;600;700;800&family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@500;700&display=swap');
.fhg-shell {
  background: #fff;
  border: 1px solid #eef2f6;
  border-radius: 22px;
  color: #0f172a;
  padding: 24px;
  box-shadow: 0 10px 28px rgba(15,23,42,.06);
  overflow: hidden;
}
.fhg-topline { display: flex; align-items: flex-start; justify-content: space-between; gap: 18px; margin-bottom: 22px; flex-wrap: wrap; }
.fhg-eyebrow { display: flex; align-items: center; gap: 10px; color: #94a3b8; font-family: 'JetBrains Mono', monospace; font-size: 10.5px; font-weight: 700; letter-spacing: .14em; text-transform: uppercase; }
.fhg-eyebrow-dot { width: 6px; height: 6px; border-radius: 50%; background: #f2b84b; box-shadow: 0 0 12px rgba(242,184,75,.9); }
.fhg-nav { display: flex; align-items: center; gap: 14px; margin-top: 13px; flex-wrap: wrap; }
.fhg-back { display: inline-flex; align-items: center; gap: 7px; padding: 9px 16px; border-radius: 12px; background: #f8fafc; border: 1px solid #cbd5e1; color: #64748b; font-family: 'Inter', sans-serif; font-size: 13px; font-weight: 700; transition: border-color .2s ease, color .2s ease, opacity .2s ease, background .2s ease; }
.fhg-back:not(:disabled):hover { background: #fff; border-color: #94a3b8; color: #0f172a; }
.fhg-back:disabled { opacity: .48; cursor: not-allowed; }
.fhg-crumbs { display: flex; align-items: center; gap: 7px; flex-wrap: wrap; font-family: 'Outfit', sans-serif; font-size: 18px; font-weight: 800; }
.fhg-crumb { border: 0; background: transparent; padding: 0; font: inherit; background-image: linear-gradient(90deg, #8e7cff, #3fd4e0); -webkit-background-clip: text; background-clip: text; color: transparent; }
.fhg-crumb-node { border: 0; background: transparent; padding: 0; font: inherit; }
.fhg-hint { color: #92400e; background: #fffbeb; border: 1px solid #fde68a; border-radius: 999px; box-shadow: 0 6px 18px rgba(245,158,11,.12); font-size: 12.5px; font-weight: 800; padding: 9px 16px; white-space: nowrap; }
.fhg-legend { align-items: center; background: #fff; border: 1px solid #eef2f6; border-radius: 16px; display: flex; flex-wrap: wrap; gap: 18px 26px; justify-content: center; margin: 0 0 22px; padding: 14px 18px; box-shadow: 0 6px 18px rgba(15,23,42,.04); }
.fhg-legend-item { align-items: center; color: #334155; display: flex; font-family: 'Inter', sans-serif; font-size: 13.5px; font-weight: 700; gap: 9px; }
.fhg-swatch { border-radius: 4px; height: 13px; width: 13px; }
.fhg-chart-card { background: #fff; border: 1px solid #eef2f6; border-radius: 16px; box-shadow: 0 12px 30px rgba(15,23,42,.06); padding: 30px 30px 24px; }
.fhg-chart-scroll { max-height: 540px; overflow-y: auto; padding-right: 4px; }
.fhg-row, .fhg-axis { align-items: center; display: grid; gap: 18px; grid-template-columns: minmax(96px, 138px) minmax(260px, 1fr) 68px; }
.fhg-row { margin-bottom: 26px; }
.fhg-label { color: #334155; font-family: 'Outfit', sans-serif; font-size: 16px; font-weight: 800; line-height: 1.15; text-align: right; }
.fhg-sub { color: #94a3b8; display: block; font-family: 'JetBrains Mono', monospace; font-size: 10.5px; font-weight: 500; letter-spacing: .04em; margin-top: 4px; }
.fhg-track { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; display: flex; height: 52px; overflow: hidden; position: relative; }
.fhg-track:focus-visible { outline: 2px solid #f2b84b; outline-offset: 3px; }
.fhg-seg { align-items: center; color: #fff; display: flex; flex: 0 0 auto; font-family: 'JetBrains Mono', monospace; font-size: 13.5px; font-weight: 800; height: 100%; justify-content: center; min-width: 0; overflow: hidden; position: relative; text-shadow: 0 1px 3px rgba(0,0,0,.35); transform: scaleX(0); transform-origin: left; animation: fhg-grow 1.1s cubic-bezier(.16,1,.3,1) forwards; }
.fhg-seg::after { background: linear-gradient(180deg, rgba(255,255,255,.17), rgba(255,255,255,0) 45%); content: ''; inset: 0; pointer-events: none; position: absolute; }
.fhg-shine { animation: fhg-shine 2.6s ease-in-out infinite; animation-delay: 1.3s; background: linear-gradient(90deg, transparent, rgba(255,255,255,.24), transparent); bottom: 0; left: -40%; position: absolute; top: 0; width: 35%; }
.fhg-seg span { position: relative; z-index: 1; }
.fhg-track:hover .fhg-seg { filter: brightness(1.08); }
.fhg-total { color: #334155; font-family: 'JetBrains Mono', monospace; font-size: 14px; font-weight: 800; opacity: 0; animation: fhg-fade .6s ease forwards; }
.fhg-axis { border-top: 1px solid #e2e8f0; margin-top: 6px; padding-top: 14px; }
.fhg-axis-track { height: 16px; position: relative; }
.fhg-axis-track span { color: #94a3b8; font-family: 'JetBrains Mono', monospace; font-size: 11px; position: absolute; top: 0; transform: translateX(-50%); }
.fhg-footnote { color: #94a3b8; display: flex; flex-wrap: wrap; font-size: 12.5px; gap: 12px; justify-content: space-between; margin-top: 22px; }
.fhg-footnote b { color: #475569; font-weight: 700; }
.fhg-empty { color: #94a3b8; padding: 46px 12px; text-align: center; }
@keyframes fhg-grow { to { transform: scaleX(1); } }
@keyframes fhg-shine { 0% { left: -40%; } 100% { left: 130%; } }
@keyframes fhg-fade { to { opacity: 1; } }
@media (max-width: 720px) {
  .fhg-shell { padding: 18px; }
  .fhg-chart-card { padding: 22px 16px 18px; }
  .fhg-row, .fhg-axis { grid-template-columns: 82px minmax(160px, 1fr) 46px; gap: 12px; }
  .fhg-label { font-size: 13.5px; }
  .fhg-seg { font-size: 11px; }
  .fhg-track { height: 46px; }
  .fhg-hint { white-space: normal; }
}
`;

const ForestHierarchyGraph = () => {
  const API_BASE_URL = process.env.REACT_APP_BE_API_URL;

  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [path, setPath] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [loadingComplaints, setLoadingComplaints] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API_BASE_URL}/backend/complaints/forest-hierarchy-overview`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data.status) setTree(res.data.zones || []);
        else setError(res.data.message || 'Failed to load hierarchy.');
      } catch (err) {
        setError(err.response?.data?.message || err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [API_BASE_URL]);

  const level = LEVELS[path.length];
  const divisionSelected = path.length === 3;
  const selectedDivision = path[2]?.node;

  const nodes = useMemo(() => {
    if (path.length === 0) return tree;
    if (path.length === 1) return path[0].node.circles || [];
    if (path.length === 2) return path[1].node.divisions || [];
    return [];
  }, [path, tree]);

  const loadComplaints = useCallback(async (divisionNode) => {
    setLoadingComplaints(true);
    setComplaints([]);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE_URL}/backend/complaints/complaints-by-forest-node`, {
        params: { level: 'division', id: divisionNode._id, perPage: 300, pageNo: 1 },
        headers: { Authorization: `Bearer ${token}` },
      });
      setComplaints(res.data.data || []);
    } catch (err) {
      setComplaints([]);
    } finally {
      setLoadingComplaints(false);
    }
  }, [API_BASE_URL]);

  const drillInto = (node) => {
    const clickedLevel = LEVELS[path.length];
    setPath((p) => [...p, { level: clickedLevel, node }]);
    if (clickedLevel === 'division') loadComplaints(node);
  };

  const goToCrumb = (idx) => {
    setPath((p) => p.slice(0, idx));
    setComplaints([]);
  };
  const goBack = () => goToCrumb(Math.max(0, path.length - 1));

  const activeStatuses = STATUS_ORDER.filter((s) => nodes.some((n) => (n.statusCounts?.[s] || 0) > 0));
  const usedStatuses = activeStatuses.length ? activeStatuses : ['In Progress'];
  const rowTotals = nodes.map((n) => STATUS_ORDER.reduce((sum, s) => sum + (n.statusCounts?.[s] || 0), 0));
  const maxTotal = Math.max(...rowTotals, 0);
  const maxScale = Math.max(50, Math.ceil(maxTotal / 50) * 50);
  const ticks = Array.from({ length: Math.floor(maxScale / 50) + 1 }, (_, i) => i * 50);
  const totalTracked = rowTotals.reduce((sum, count) => sum + count, 0);
  const crumbColor = (i) => LEVEL_COLOR[LEVELS[i]] || '#64748b';

  const subLabel = (node) => {
    if (level === 'zone') return `${node.circles?.length || 0} circles`;
    if (level === 'circle') return `${node.divisions?.length || 0} divisions`;
    return `${node.count || STATUS_ORDER.reduce((sum, s) => sum + (node.statusCounts?.[s] || 0), 0)} complaints`;
  };

  if (loading) return <Skeleton height={340} />;
  if (error) return <div className="alert alert-danger mb-0">{error}</div>;

  return (
    <div className="fhg-shell">
      <style>{styles}</style>
      <div className="fhg-topline">
        <div>
          <div className="fhg-eyebrow">
            <span className="fhg-eyebrow-dot" />
            Graphical Trend - drill zone to circle to division, then view status cards
          </div>
          <div className="fhg-nav">
            <button type="button" className="fhg-back" onClick={goBack} disabled={path.length === 0}>
              <Icon icon="mdi:arrow-left" /> Back
            </button>
            <div className="fhg-crumbs">
              <button type="button" className="fhg-crumb" onClick={() => goToCrumb(0)}>All Zones</button>
              {path.map((p, i) => (
                <span key={`${p.level}-${p.node._id}`} className="d-flex align-items-center gap-1">
                  <Icon icon="mdi:chevron-right" style={{ color: '#94a3b8' }} />
                  <button type="button" className="fhg-crumb-node" style={{ color: crumbColor(i) }} onClick={() => goToCrumb(i + 1)}>
                    {p.node.name}
                  </button>
                </span>
              ))}
            </div>
          </div>
        </div>
        <span className="fhg-hint">
          {divisionSelected ? 'Complaints - click a card to open' : `${LEVEL_LABEL[level]} - click a bar to drill in`}
        </span>
      </div>

      {divisionSelected ? (
        loadingComplaints ? (
          <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-3 g-3">
            {Array.from({ length: 6 }).map((_, i) => (<div className="col" key={i}><Skeleton height={130} /></div>))}
          </div>
        ) : complaints.length === 0 ? (
          <div className="fhg-empty">
            <Icon icon="mdi:file-search-outline" width="40" /><div>No complaints in {selectedDivision?.name || 'this division'}.</div>
          </div>
        ) : (
          <>
            <div className="mb-2 fw-semibold" style={{ fontSize: 14, color: '#334155' }}>
              {selectedDivision?.name} <span style={{ color: '#94a3b8' }}>- {complaints.length} complaint(s)</span>
            </div>
            <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-3 g-3" style={{ maxHeight: 560, overflowY: 'auto' }}>
              {complaints.map((c) => {
                const col = statusColor(c.status);
                return (
                  <div className="col" key={c._id}>
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => openReport(c._id)}
                      onKeyDown={(e) => { if (e.key === 'Enter') openReport(c._id); }}
                      style={{
                        background: '#fff', border: '1px solid #eef2f6', borderLeft: `5px solid ${col}`,
                        borderRadius: 12, padding: 14, cursor: 'pointer', height: '100%',
                        boxShadow: '0 4px 14px rgba(15,23,42,.06)', transition: 'transform .15s, box-shadow .15s',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 10px 24px rgba(15,23,42,.12)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(15,23,42,.06)'; }}
                    >
                      <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                        <span className="fw-bold" style={{ color: '#0f172a' }}>{c.complaint_number}</span>
                        <span className="badge" style={{ background: `${col}1f`, color: col, fontWeight: 700 }}>{statusLabel(c.status)}</span>
                      </div>
                      <div style={{ fontSize: 12.5, color: '#475569', lineHeight: 1.6 }}>
                        <div><b>Complainant:</b> {c.complainant_name || '-'}</div>
                        <div><b>Referred by:</b> {c.referred_by || '-'}</div>
                        <div><b>Date:</b> {c.date ? new Date(c.date).toLocaleDateString() : '-'}</div>
                        <div><b>Nature:</b> {c.nature || '-'}</div>
                        <div><b>Priority:</b> {c.priority || '-'}</div>
                      </div>
                      <div className="mt-2 d-flex align-items-center gap-1" style={{ color: col, fontWeight: 700, fontSize: 12 }}>
                        Open report <Icon icon="mdi:open-in-new" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )
      ) : nodes.length === 0 ? (
        <div className="fhg-empty">Nothing to show here.</div>
      ) : (
        <>
          <div className="fhg-legend">
            {usedStatuses.map((status) => {
              const [from, to] = statusGradient(status);
              return (
                <div className="fhg-legend-item" key={status}>
                  <span className="fhg-swatch" style={{ background: `linear-gradient(135deg, ${from}, ${to})` }} />
                  {statusLabel(status)}
                </div>
              );
            })}
          </div>
          <div className="fhg-chart-card">
            <div className="fhg-chart-scroll">
              {nodes.map((node, rowIndex) => {
                const total = rowTotals[rowIndex] || 0;
                return (
                  <div className="fhg-row" key={node._id || node.name}>
                    <div className="fhg-label">
                      {node.name}
                      <span className="fhg-sub">{subLabel(node)}</span>
                    </div>
                    <div
                      className="fhg-track"
                      role="button"
                      tabIndex={0}
                      title={`Open ${node.name}`}
                      onClick={() => drillInto(node)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          drillInto(node);
                        }
                      }}
                    >
                      {usedStatuses.map((status) => {
                        const value = node.statusCounts?.[status] || 0;
                        if (!value) return null;
                        const [from, to] = statusGradient(status);
                        const pct = (value / maxScale) * 100;
                        return (
                          <div
                            className="fhg-seg"
                            key={status}
                            style={{
                              width: `${pct}%`,
                              background: `linear-gradient(180deg, ${from}, ${to})`,
                              animationDelay: `${0.15 + rowIndex * 0.12}s`,
                            }}
                            title={`${statusLabel(status)}: ${value}`}
                          >
                            <span>{value}</span>
                            <div className="fhg-shine" />
                          </div>
                        );
                      })}
                    </div>
                    <div className="fhg-total" style={{ animationDelay: `${0.9 + rowIndex * 0.12}s` }}>{total}</div>
                  </div>
                );
              })}
            </div>
            <div className="fhg-axis">
              <div />
              <div className="fhg-axis-track">
                {ticks.map((tick) => (
                  <span key={tick} style={{ left: `${(tick / maxScale) * 100}%` }}>{tick}</span>
                ))}
              </div>
              <div />
            </div>
            <div className="fhg-footnote">
              <span><b>{nodes.length} {LEVEL_LABEL[level].toLowerCase()}</b> - <b>{totalTracked}</b> total complaints tracked</span>
              <span>Click any bar to continue the drill-down</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ForestHierarchyGraph;
